// @flow
import React, { Component } from "react";

import type { ErrorsType } from "../../types/UserFormType";
import {
  StyledTextField,
  CancelButton,
  SubmitButton,
  SingleCheckBox
} from "imagus-components";
import { ErrosDisplay, setErrorMsg } from "../../containers/ErrosDisplay";
import { RemoveAllSpace } from "../../../global/functions/MultilLineStrings";
import InfiniteCalendarSelect from "../../../global/components/InfiniteCalendarSelect";
import { DatetoStartTimeAppend } from "../../../global/functions/DateTimeAmend";
import {
  GraphqlLineBreak,
  utf8OutRangeFilter
} from "../../../global/functions/MultilLineStrings";

import Grid from "@material-ui/core/Grid";

type AddVersionFormStatesType = {
  errors: ErrorsType,
  loading: boolean,
  versionid: string,
  link: string,
  releaseDate: Date | null,
  notes: string,
  isCurrent: boolean,
  isPublic: boolean,
  isBeta: boolean
};

type AddVersionFormPropsType = {
  initialForm: { [key: string]: * },
  isEdit: boolean,
  onClose: Function,
  AddVersion: Function,
  UpdateVersion: Function
};

const initialform: AddVersionFormStatesType = {
  errors: {},
  loading: false,
  versionid: "",
  link: "",
  releaseDate: DatetoStartTimeAppend(new Date()),
  notes: "",
  isCurrent: false,
  isPublic: false,
  isBeta: false
};

class AddVersionForm extends Component<
  AddVersionFormPropsType,
  AddVersionFormStatesType
> {
  constructor(props: AddVersionFormPropsType) {
    super(props);
    this.submit = this.submit.bind(this);
    this.state = initialform;
  }

  componentDidMount() {
    this.setState({ ...this.props.initialForm, errors: {} });
  }

  handleTextChange = (e: SyntheticInputEvent<*>): * => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState({ errors: {}, [name]: value });
  };

  handleCheckBoxChange = (
    e: Event & { currentTarget: HTMLInputElement }
  ): * => {
    const name = e.currentTarget.name;
    const value = !this.state[name];
    this.setState(
      {
        [name]: value
      },
      (): * => {
        if (
          name === "isPublic" &&
          value === false &&
          this.state.isCurrent === true
        ) {
          this.setState({ isCurrent: false }); // if set to not public, cancel current status
        }

        if (name === "isCurrent" && value === true) {
          if (this.state.isPublic === false) {
            this.setState({ isPublic: true }); // current version must be public
          }
          if (this.state.isBeta === true) {
            this.setState({ isBeta: false }); // current version can not be beta
          }
        }

        if (
          name === "isBeta" &&
          value === true &&
          this.state.isCurrent === true
        ) {
          this.setState({ isCurrent: false }); // beta version can not be the current one
        }
      }
    );
  };

  syncDateChange = (name: string, date: Date): * => {
    this.setState({ [name]: date });
  };

  submit = (e: Event): * => {
    e.preventDefault();
    e.stopPropagation();
    if (Object.keys(this.state.errors).length === 0) {
      this.setState((): * => ({ loading: true }));

      if (this.props.isEdit) {
        this.props.UpdateVersion(
          RemoveAllSpace(this.state.versionid),
          RemoveAllSpace(this.state.link),
          this.state.releaseDate,
          GraphqlLineBreak(utf8OutRangeFilter(this.state.notes)),
          this.state.isPublic,
          this.state.isCurrent,
          this.state.isBeta,
          this.submitDone,
          this.submitFail
        );
      } else {
        this.props.AddVersion(
          RemoveAllSpace(this.state.versionid),
          RemoveAllSpace(this.state.link),
          this.state.releaseDate,
          GraphqlLineBreak(utf8OutRangeFilter(this.state.notes)),
          this.state.isPublic,
          this.state.isCurrent,
          this.state.isBeta,
          this.submitDone,
          this.submitFail
        );
      }
    }
  };

  submitDone = () => {
    this.props.onClose();
  };

  submitFail = (errors: ErrorsType): * => {
    this.setState({
      errors: errors,
      loading: false
    });
  };

  render(): React$Element<*> {
    const errors = this.state.errors;
    const loading = this.state.loading;
    return (
      <form onSubmit={(!loading && this.submit) || undefined}>
        <Grid container spacing={8}>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="New Version Id"
              name="versionid"
              value={this.state.versionid || ""}
              onChange={this.handleTextChange}
              inpuType="text"
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="Link"
              name="link"
              value={this.state.link || ""}
              onChange={this.handleTextChange}
              inpuType="text"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfiniteCalendarSelect
              isRequired={true}
              label="Release Date"
              name="releaseDate"
              value={this.state.releaseDate}
              syncDateChange={this.syncDateChange}
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              isRequired={true}
              label="Release Notes"
              name="notes"
              value={this.state.notes}
              onChange={this.handleTextChange}
              inpuType="text"
              rows={4}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SingleCheckBox
              name="isPublic"
              value={this.state.isPublic}
              onChange={this.handleCheckBoxChange}
              label="Open to the Public"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SingleCheckBox
              name="isCurrent"
              value={this.state.isCurrent}
              onChange={this.handleCheckBoxChange}
              label="Is the Current Version"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SingleCheckBox
              name="isBeta"
              value={this.state.isBeta}
              onChange={this.handleCheckBoxChange}
              label="Beta Version"
            />
          </Grid>
          <Grid item xs={12}>
            <ErrosDisplay errors={errors} form="add version" />
          </Grid>
          <Grid item xs={12}>
            <div className="form-right-btn-block">
              <CancelButton onClick={this.props.onClose} />
              <SubmitButton
                loading={loading}
                disabled={Object.keys(errors).length > 0}
              />
            </div>
          </Grid>
        </Grid>
      </form>
    );
  }
}

export default AddVersionForm;
